// @flow
import styled from '@emotion/styled';
import Button from '@tablekit/button';
import { createHeader } from '@tablekit/typography';
import { color, COLORS, SPACING, typography } from '@tablekit/theme';
import { LAPTOP_BREAKPOINT, MAX_WIDTH } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import type { BlokComponentType, SimpleContentType } from 'components/types';
import RichText from 'components/RichText';
import StoryblokEditor from 'components/editable/StoryblokEditor';
import { getPageContext } from 'components/PageContext';
import StoryblokLink from 'components/Link';

const backgroundFixHeight = '200px';
const curveHeight = 15;
const curveHeightDecimal = curveHeight / 100;

const CallToActionBackgroundFillerBase = styled.div`
  height: ${SPACING.L6};
  background: ${({ isWhite }) => (isWhite ? 'white' : 'transparent')};
`;

const CallToActionBackgroundFiller = styled(CallToActionBackgroundFillerBase)`
  height: calc(${backgroundFixHeight} + ${SPACING.L6});
  position: relative;
  width: 100vw;
`;

export const CallToActionBackground = (props: { isWhite?: boolean }) => {
  const {
    layout: { call_to_action: callToAction }
  } = getPageContext();
  if (!callToAction || callToAction.length === 0) {
    return <CallToActionBackgroundFillerBase {...props} />;
  }
  return <CallToActionBackgroundFiller {...props} />;
};

const CallToActionContent = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: ${curveHeight}%;
  padding: 0 ${SPACING.L4};
  @media ${LAPTOP_BREAKPOINT} {
    margin: ${curveHeight / 2}%;
    max-width: ${MAX_WIDTH};
  }
`;

const CallToActionBanner = styled.div`
  display: flex;
  margin-top: -${backgroundFixHeight};
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  color: ${COLORS.GRAY.L1};

  background: ${color('primary')};
  background: linear-gradient(
    to bottom right,
    ${color('primary')} 20%,
    ${color('primary2')} 80%
  );

  clip-path: url(#footer-cta-top-border-mobile);
  @media ${LAPTOP_BREAKPOINT} {
    clip-path: url(#footer-cta-top-border-desktop);
  }
`;

const CallToActionTitle = styled(
  createHeader('h3', { [LAPTOP_BREAKPOINT]: 'L7', default: 'L6' })
)`
  color: ${COLORS.GRAY.L1};
  margin-bottom: ${SPACING.L4};
  text-transform: uppercase;
  max-width: ${columnWidth(12)}%;
  @media ${LAPTOP_BREAKPOINT} {
    margin-bottom: ${SPACING.L2};
    width: ${columnWidth(8)}%;
  }
`;

const CallToActionBody = styled(RichText)`
  margin-bottom: ${SPACING.L6};
  max-width: ${columnWidth(12)}%;
  ${typography('L4')};

  @media ${LAPTOP_BREAKPOINT} {
    max-width: ${columnWidth(6)}%;
  }
`;

export default function CallToAction({
  blok
}: {
  blok?: BlokComponentType<SimpleContentType>
}) {
  if (!blok) return null;
  const { title, body, link, link_text: linkText } = blok;
  return (
    <StoryblokEditor content={blok}>
      <CallToActionBanner>
        <svg width="0" height="0" viewBox="0 0 100 100">
          <defs>
            <clipPath
              id="footer-cta-top-border-desktop"
              clipPathUnits="objectBoundingBox"
            >
              <path
                d={`M 0 ${curveHeightDecimal} q 0.5 -${
                  curveHeightDecimal * 2
                } 1 0 L 1 1.5 L 0 1.5 Z`}
              />
            </clipPath>
            <clipPath
              id="footer-cta-top-border-mobile"
              clipPathUnits="objectBoundingBox"
            >
              <path
                d={`M 0 ${
                  curveHeightDecimal / 2
                } q 0.5 -${curveHeightDecimal} 1 0 L 1 1.5 L 0 1.5 Z`}
              />
            </clipPath>
          </defs>
        </svg>
        <CallToActionContent>
          <CallToActionTitle>{title}</CallToActionTitle>
          <CallToActionBody richText={body} />
          <Button appearance="inverse" component={StoryblokLink} link={link}>
            {linkText}
          </Button>
        </CallToActionContent>
      </CallToActionBanner>
    </StoryblokEditor>
  );
}
